import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  getDoc,
  deleteField
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/shop_logo.png';
import { SHOP_NAME } from "../Variables.js";

Modal.setAppElement('#root');

const Bills = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [bills, setBills] = useState([]);
  const [filteredBills, setFilteredBills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  const [newBill, setNewBill] = useState({
    vendorId: '',
    billNumber: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    amount: 0,
    expenseAccountId: '',
    purchaseOrderId: '',
    paymentStatus: 'Unpaid',
    scheduledPaymentDate: '',
    userId: user?.uid || ''
  });
  const [editingBill, setEditingBill] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFetchingBills, setIsFetchingBills] = useState(true);
  const [hasFetchedBills, setHasFetchedBills] = useState(false);

  // PDF Date Range Modal
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const billsCollectionRef = collection(db, "bills");
  const vendorsCollectionRef = collection(db, "vendors");
  const accountsCollectionRef = collection(db, "accounts");
  const purchaseOrdersCollectionRef = collection(db, "purchaseOrders");

  useEffect(() => {
    const loadData = async () => {
      if (user && userData) {
        await fetchVendors();
        await fetchExpenseAccounts();
        await fetchPurchaseOrders();
        await fetchBills();
      }
    };
    loadData();
  }, [user, userData]);

  useEffect(() => {
    const filtered = bills.filter((bill) =>
      bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.vendorName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBills(filtered);
  }, [searchTerm, bills]);

  useEffect(() => {
    const today = new Date();
    bills.forEach(bill => {
      if (bill.dueDate) {
        const dueDate = new Date(bill.dueDate);
        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        if (daysUntilDue <= 3 && daysUntilDue > 0 && bill.paymentStatus === 'Unpaid') {
          console.log(`Reminder: Bill ${bill.billNumber} is due in ${daysUntilDue} days.`);
        }
      }
    });
  }, [bills]);

  const fetchVendors = async () => {
    if (!user?.uid) return;
    try {
      const q = query(vendorsCollectionRef, where("active", "==", true));
      const data = await getDocs(q);
      const vendorList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to fetch vendors");
      setShowModal(true);
    }
  };

  const fetchExpenseAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Expense"));
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setExpenseAccounts(accountList);
    } catch (error) {
      console.error("Error fetching expense accounts:", error);
      setError("Failed to fetch expense accounts");
      setShowModal(true);
    }
  };

  const fetchPurchaseOrders = async () => {
    if (!user?.uid) return;
    try {
      const q = query(purchaseOrdersCollectionRef);
      const data = await getDocs(q);
      const poList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setPurchaseOrders(poList);
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      setError("Failed to fetch purchase orders");
      setShowModal(true);
    }
  };

  const fetchBills = async () => {
    if (!user?.uid) return;

    setIsFetchingBills(true);
    try {
      const q = query(billsCollectionRef);
      const snapshot = await getDocs(q);

      const billList = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // Use the vendorName that is already saved on the bill
          vendorName: data.vendorName || "Unknown",
          vendorDisplayName: data.vendorName || "Unknown",
        };
      });

      setBills(billList);
      setHasFetchedBills(true);
    } catch (error) {
      console.error("Error fetching bills:", error);
      setError("Failed to fetch bills");
      setShowModal(true);
    } finally {
      setIsFetchingBills(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewBill({ ...newBill, [name]: value });
  };

  const handleAddBill = async () => {
    if (!newBill.vendorId || !newBill.billNumber.trim() || newBill.amount <= 0 || !newBill.dueDate) {
      setError("Vendor, bill number, positive amount, and due date are required");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      const vendor = vendors.find(v => v.id === newBill.vendorId);
      if (!vendor) {
        setError("Selected vendor not found");
        setShowModal(true);
        return;
      }
      const docRef = await addDoc(billsCollectionRef, {
        ...newBill,
        vendorName: vendor.name,
        userId: user?.uid,
        amount: parseFloat(newBill.amount),
        expenseAccountId: newBill.expenseAccountId || vendor?.defaultExpenseAccountId || '',
        createdAt: Timestamp.now()
      });
      const billId = docRef.id;
      if (newBill.purchaseOrderId) {
        const poDoc = doc(db, "purchaseOrders", newBill.purchaseOrderId);
        const poSnap = await getDoc(poDoc);
        if (!poSnap.exists()) {
          throw new Error("Purchase Order not found");
        }
        const poData = poSnap.data();
        if (poData.vendorId !== newBill.vendorId) {
          throw new Error("Vendor mismatch");
        }
        const amountDiff = Math.abs(poData.amount - parseFloat(newBill.amount));
        if (amountDiff > 0.01) {
          throw new Error(`Amount mismatch: ₦${amountDiff.toFixed(2)} difference`);
        }
        if (poData.matchedBillId) {
          throw new Error("Purchase Order already matched to another bill");
        }
        await updateDoc(poDoc, {
          matchedBillId: billId,
          matchedBillNumber: newBill.billNumber
        });
      }
      setSuccessMessage("Bill added successfully");
      await fetchBills();
      await fetchPurchaseOrders();
      resetNewBill();
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Add bill error:", error);
      setError("Failed to add bill: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateBill = async () => {
    setUpdate(true);
    try {
      const vendor = vendors.find(v => v.id === newBill.vendorId);
      if (!vendor) {
        throw new Error("Selected vendor not found");
      }
      const billDoc = doc(db, "bills", editingBill.id);
      const oldPOId = editingBill.purchaseOrderId;
      const newPOId = newBill.purchaseOrderId;
      if (oldPOId !== newPOId) {
        if (oldPOId) {
          const oldPODoc = doc(db, "purchaseOrders", oldPOId);
          await updateDoc(oldPODoc, {
            matchedBillId: deleteField(),
            matchedBillNumber: deleteField()
          });
        }
        if (newPOId) {
          const newPODoc = doc(db, "purchaseOrders", newPOId);
          const newPOSnap = await getDoc(newPODoc);
          if (!newPOSnap.exists()) {
            throw new Error("Purchase Order not found");
          }
          const poData = newPOSnap.data();
          if (poData.vendorId !== newBill.vendorId) {
            throw new Error("Vendor mismatch");
          }
          const amountDiff = Math.abs(poData.amount - parseFloat(newBill.amount));
          if (amountDiff > 0.01) {
            throw new Error(`Amount mismatch: ₦${amountDiff.toFixed(2)} difference`);
          }
          if (poData.matchedBillId) {
            throw new Error("Purchase Order already matched to another bill");
          }
          await updateDoc(newPODoc, {
            matchedBillId: editingBill.id,
            matchedBillNumber: newBill.billNumber
          });
        }
      }
      await updateDoc(billDoc, {
        ...newBill,
        vendorName: vendor.name,
        amount: parseFloat(newBill.amount)
      });
      setSuccessMessage("Bill updated successfully");
      await fetchBills();
      await fetchPurchaseOrders();
      resetNewBill();
      setEditingBill(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error("Update bill error:", error);
      setError("Failed to update bill: " + error.message);
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewBill = () => {
    setNewBill({
      vendorId: '',
      billNumber: '',
      date: new Date().toISOString().split('T')[0],
      dueDate: '',
      amount: 0,
      expenseAccountId: '',
      purchaseOrderId: '',
      paymentStatus: 'Unpaid',
      scheduledPaymentDate: '',
      userId: user?.uid || ''
    });
  };

  const handleEditBill = (billToEdit) => {
    setEditingBill(billToEdit);
    setNewBill({
      vendorId: billToEdit.vendorId,
      billNumber: billToEdit.billNumber,
      date: billToEdit.date,
      dueDate: billToEdit.dueDate,
      amount: billToEdit.amount,
      expenseAccountId: billToEdit.expenseAccountId,
      purchaseOrderId: billToEdit.purchaseOrderId || '',
      paymentStatus: billToEdit.paymentStatus,
      scheduledPaymentDate: billToEdit.scheduledPaymentDate || '',
      userId: billToEdit.userId
    });
    setIsEditModalOpen(true);
  };

  const formatAmount = (value) => {
    const num = Number(value) || 0;
    return `₦${num.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

    const formatAmount1 = (value) => {
    const num = Number(value) || 0;
    return `N${num.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // PDF Helpers
  const dateFormatter = new Intl.DateTimeFormat('en-NG', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });

  const formatDateTime = (date) => {
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).replace(',', '');
  };

  const formatDateForFilename = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDateRange = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      setShowModal(true);
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      setShowModal(true);
      return false;
    }
    return true;
  };

  const generateBillsPDF = () => {
    if (!validateDateRange()) return;

    const dateFilteredBills = bills.filter(bill => {
      const billDate = new Date(bill.date);
      return billDate >= new Date(startDate) && billDate <= new Date(endDate);
    });

    if (dateFilteredBills.length === 0) {
      setError("No bills found for the selected date range");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";
    const storeName = SHOP_NAME;

    let currentY = 10;
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, 35, currentY + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Bills Report", 35, currentY + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, currentY + 28);
    currentY += 33;

    const sortedBills = [...dateFilteredBills].sort((a, b) => new Date(a.date) - new Date(b.date));

    // === CALCULATE TOTALS ===
    const totalPaid = sortedBills
      .filter(b => b.paymentStatus === 'Paid')
      .reduce((sum, b) => sum + parseFloat(b.amount), 0);

    const totalUnpaid = sortedBills
      .filter(b => b.paymentStatus === 'Unpaid')
      .reduce((sum, b) => sum + parseFloat(b.amount), 0);

    const totalPartiallyPaid = sortedBills
      .filter(b => b.paymentStatus === 'Partially Paid')
      .reduce((sum, b) => sum + parseFloat(b.amount), 0);

    const grandTotal = totalPaid + totalUnpaid + totalPartiallyPaid;

    // === MAIN TABLE DATA ===
    const tableData = sortedBills.map((bill, index) => {
      const account = expenseAccounts.find(a => a.id === bill.expenseAccountId);
      return [
        (index + 1).toString(),
        bill.billNumber,
        bill.vendorName || 'Unknown',
        dateFormatter.format(new Date(bill.date)),
        dateFormatter.format(new Date(bill.dueDate)),
        formatAmount1(bill.amount),
        account?.name || 'None',
        bill.paymentStatus
      ];
    });

    // === DRAW MAIN TABLE ===
    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Bill #', 'Vendor', 'Date', 'Due Date', 'Amount', 'Account', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 4, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 28 },
        2: { cellWidth: 40 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 28 },
        6: { cellWidth: 35 },
        7: { cellWidth: 18 }
      },
      didDrawPage: (data) => {
        currentY = data.table.finalY || data.cursor.y;
        if (data.pageNumber < doc.internal.getNumberOfPages()) {
          doc.setPage(data.pageNumber + 1);
          doc.addImage(Logo, "PNG", 14, 10, 15, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(storeName, 35, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "normal");
          doc.text("Bills Report", 35, 23);
          doc.setFontSize(10);
          doc.text(`Generated by: ${fullName}`, 35, 28);
          doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);
          doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, 38);
          currentY = 44;
        }
      }
    });

    // === ADD SUMMARY TABLE AFTER MAIN TABLE ===
    currentY += 10;

    autoTable(doc, {
      startY: currentY,
      head: [['Summary', 'Amount']],
      body: [
        ['Total Paid', formatAmount1(totalPaid)],
        ['Total Unpaid', formatAmount1(totalUnpaid)],
        ['Total Partially Paid', formatAmount1(totalPartiallyPaid)],
        ['Grand Total', formatAmount1(grandTotal)]
      ],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { fontSize: 10, textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: 'bold' },
        1: { cellWidth: 60, halign: 'right' }
      },
      margin: { left: 4, right: 10 }
    });

    // === PAGE NUMBERS ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    // === SAVE PDF ===
    doc.save(`bills_report_${formatDateForFilename(new Date(startDate))}_to_${formatDateForFilename(new Date(endDate))}.pdf`);
    setIsDateRangeModalOpen(false);
    setStartDate('');
    setEndDate('');
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingBills && !hasFetchedBills) {
      return <p className="loading-text">Loading bills...</p>;
    }
    
    if (filteredBills.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No bills match your search.</p>;
      } else {
        return <p className="loading-text">No bills found. Add your first bill.</p>;
      }
    }
    
    return filteredBills.map((billItem) => {
      const matchedPO = purchaseOrders.find(po => 
        po.id === billItem.purchaseOrderId
      );
      
      return (
        <div key={billItem.id} className="class-item">
          <div className="column gap-1">
            <p>
              <strong>{billItem.billNumber}</strong><br />
              <span>Vendor: {billItem.vendorName}</span><br />
              <span>{formatAmount(billItem.amount)}</span><br />
              <span>Due Date: {billItem.dueDate}</span><br />
              <span>Account: {expenseAccounts.find(a => a.id === billItem.expenseAccountId)?.name || 'None'}</span>
              {matchedPO ? (
                <span style={{color: 'green'}}>
                  <br />PO: <strong>{matchedPO.poNumber}</strong>
                </span>
              ) : (
                <span style={{color: 'orange'}}>
                  <br />No Purchase Order
                </span>
              )}
              <br />
              <span className="size5 font-light-blue-cash bold">{billItem.paymentStatus}</span>
            </p>                        
          </div>
          <div className="row align-center justify-center gap-15">
            <button className="list-btn" onClick={() => handleEditBill(billItem)}>
              <i className="fas fa-edit"></i>
            </button>
            {matchedPO && (
              <button className="list-btn" style={{color: 'green'}}>
                <i className="fas fa-link"></i>
              </button>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Bills</h2>
            <Link to="#" className="home-link" onClick={() => {
              fetchBills();
              fetchPurchaseOrders();
            }}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Bill</button>
              <button className="login-button login-button-d caps" onClick={() => setIsDateRangeModalOpen(true)}>Download PDF</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by bill number or vendor"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Bill"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Bill</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="vendorId" className="input-label">Vendor *</label>
          <select
            name="vendorId"
            className="input-field"
            value={newBill.vendorId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Vendor</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>
          
          <label htmlFor="billNumber" className="input-label">Bill Number *</label>
          <input
            type="text"
            name="billNumber"
            className="input-field"
            placeholder="Bill Number"
            value={newBill.billNumber}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="date" className="input-label">Date *</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newBill.date}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="dueDate" className="input-label">Due Date *</label>
          <input
            type="date"
            name="dueDate"
            className="input-field"
            value={newBill.dueDate}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="amount" className="input-label">Amount *</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newBill.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="0.01"
          />
          
          <label htmlFor="expenseAccountId" className="input-label">Expense Account</label>
          <select
            name="expenseAccountId"
            className="input-field"
            value={newBill.expenseAccountId}
            onChange={handleInputChange}
          >
            <option value="">Select Expense Account (Optional)</option>
            {expenseAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
          
          <label htmlFor="purchaseOrderId" className="input-label">Purchase Order</label>
          <select
            name="purchaseOrderId"
            className="input-field"
            value={newBill.purchaseOrderId}
            onChange={handleInputChange}
          >
            <option value="">Select Purchase Order (Optional)</option>
            {purchaseOrders.map(po => (
              <option key={po.id} value={po.id}>{po.poNumber} - {po.vendorName}</option>
            ))}
          </select>
          
          <label htmlFor="paymentStatus" className="input-label">Payment Status</label>
          <select
            name="paymentStatus"
            className="input-field"
            value={newBill.paymentStatus}
            onChange={handleInputChange}
          >
            <option value="Unpaid">Unpaid</option>
            <option value="Paid">Paid</option>
            <option value="Partially Paid">Partially Paid</option>
          </select>
          
          <label htmlFor="scheduledPaymentDate" className="input-label">Scheduled Payment</label>
          <input
            type="date"
            name="scheduledPaymentDate"
            className="input-field"
            value={newBill.scheduledPaymentDate}
            onChange={handleInputChange}
          />
          
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddBill}>Add Bill</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewBill();
          setEditingBill(null);
        }}
        contentLabel="Edit Bill"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Bill</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="vendorId" className="input-label">Vendor *</label>
          <select
            name="vendorId"
            className="input-field"
            value={newBill.vendorId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Vendor</option>
            {vendors.map(vendor => (
              <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
            ))}
          </select>

          <label htmlFor="billNumber" className="input-label">Bill Number *</label>
          <input
            type="text"
            name="billNumber"
            className="input-field"
            placeholder="Bill Number"
            value={newBill.billNumber}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="date" className="input-label">Date *</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newBill.date}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="dueDate" className="input-label">Due Date *</label>
          <input
            type="date"
            name="dueDate"
            className="input-field"
            value={newBill.dueDate}
            onChange={handleInputChange}
            required
          />
          
          <label htmlFor="amount" className="input-label">Amount *</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newBill.amount}
            onChange={handleInputChange}
            required
            min="0"
            step="0.01"
          />
          
          <label htmlFor="expenseAccountId" className="input-label">Expense Account</label>
          <select
            name="expenseAccountId"
            className="input-field"
            value={newBill.expenseAccountId}
            onChange={handleInputChange}
          >
            <option value="">Select Expense Account (Optional)</option>
            {expenseAccounts.map(acc => (
              <option key={acc.id} value={acc.id}>{acc.name}</option>
            ))}
          </select>
          
          <label htmlFor="purchaseOrderId" className="input-label">Purchase Order</label>
          <select
            name="purchaseOrderId"
            className="input-field"
            value={newBill.purchaseOrderId}
            onChange={handleInputChange}
          >
            <option value="">Select Purchase Order (Optional)</option>
            {purchaseOrders.map(po => (
              <option key={po.id} value={po.id}>{po.poNumber} - {po.vendorName}</option>
            ))}
          </select>
          
          <label htmlFor="paymentStatus" className="input-label">Payment Status</label>
          <select
            name="paymentStatus"
            className="input-field"
            value={newBill.paymentStatus}
            onChange={handleInputChange}
          >
            <option value="Unpaid">Unpaid</option>
            <option value="Paid">Paid</option>
            <option value="Partially Paid">Partially Paid</option>
          </select>
          
          <label htmlFor="scheduledPaymentDate" className="input-label">Scheduled Payment</label>
          <input
            type="date"
            name="scheduledPaymentDate"
            className="input-field"
            value={newBill.scheduledPaymentDate}
            onChange={handleInputChange}
          />
          
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewBill();
              setEditingBill(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateBill}>Update Bill</button>
          </div>
        </form>
      </Modal>

      {/* Date Range Modal for PDF */}
      <Modal
        isOpen={isDateRangeModalOpen}
        onRequestClose={() => {
          setIsDateRangeModalOpen(false);
          setStartDate('');
          setEndDate('');
        }}
        contentLabel="Select Date Range for Bills PDF"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Select Date Range for PDF</h2>
        <form className="session-form">
          <label className="input-label">Start Date</label>
          <input
            type="date"
            className="input-field"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <label className="input-label">End Date</label>
          <input
            type="date"
            className="input-field"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => {
                setIsDateRangeModalOpen(false);
                setStartDate('');
                setEndDate('');
              }}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={generateBillsPDF}
            >
              Generate PDF
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default Bills;